import React, { useEffect, useRef, useState, ChangeEvent } from "react";
import QRCodeStyling, {
  DrawType,
  TypeNumber,
  Mode,
  ErrorCorrectionLevel,
  DotType,
  CornerSquareType,
  CornerDotType,
  Options
} from "qr-code-styling";
import { Box, Card, FormControl, TextField, Button, Typography, MenuItem, Select, InputLabel, SelectChangeEvent, CircularProgress, FormHelperText } from "@mui/material";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { MuiColorInput } from "mui-color-input";
import { AppColor } from "../../../../types/Constants";
import Skeleton from "react-loading-skeleton";
import { useAppSelector } from "../../../../utils/hooks";

interface QRCodeGenProps {
    subdomain?: string;
}

const QRCodeGen: React.FC<QRCodeGenProps> = ({ subdomain }) => {

    const businessGroupState = useAppSelector(state => state.businessGroup);
    const [themeColor, setThemeColor] = useState("#ffffff");
    const [dotColor, setDotColor] = useState("#0C2340");
    const [currentIconFile, setCurrentIconFile] = useState<File | null>(null);
    const [iconError, setIconError] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [options, setOptions] = useState<Options>({
        width: 250,
        height: 250,
        type: 'svg' as DrawType,
        data: "https://ar.skidattl.com/?id=" + subdomain,
        image: currentIconFile ? URL.createObjectURL(currentIconFile) : '/skidattl-icon.png',
        margin: 10,
        qrOptions: {
          typeNumber: 0 as TypeNumber,
          mode: 'Byte' as Mode,
          errorCorrectionLevel: 'Q' as ErrorCorrectionLevel
        },
        imageOptions: {
          hideBackgroundDots: true,
          imageSize: 0.5,
          margin: 0,
          crossOrigin: 'anonymous',
        },
        dotsOptions: {
          color: '#0C2340',
          // gradient: {
          //   type: 'linear', // 'radial'
          //   rotation: 0,
          //   colorStops: [{ offset: 0, color: '#8688B2' }, { offset: 1, color: '#77779C' }]
          // },
          type: 'square' as DotType
        },
        backgroundOptions: {
          color: '',
          // gradient: {
          //   type: 'linear', // 'radial'
          //   rotation: 0,
          //   colorStops: [{ offset: 0, color: '#ededff' }, { offset: 1, color: '#e6e7ff' }]
          // },
        },
        cornersSquareOptions: {
          color: '#0C2340',
          type: 'rounded' as CornerDotType,
          // gradient: {
          //   type: 'linear', // 'radial'
          //   rotation: 180,
          //   colorStops: [{ offset: 0, color: '#25456e' }, { offset: 1, color: '#4267b2' }]
          // },
        },
        cornersDotOptions: {
          color: '#0C2340',
          type: 'dot' as CornerDotType,
          // gradient: {
          //   type: 'linear', // 'radial'
          //   rotation: 180,
          //   colorStops: [{ offset: 0, color: '#00266e' }, { offset: 1, color: '#4060b3' }]
          // },
        }
      });

    const [fileExt, setFileExt] = useState('png');
    const qrCode = useRef<QRCodeStyling | null>(null);
    const ref = useRef<HTMLDivElement>(null);

    const downloadWidth = 500;
    const downloadHeight = 500;
    
    // const useDebounce = (value: any, delay: number) => {
    //     const [debouncedValue, setDebouncedValue] = useState(value);
    
    //     useEffect(() => {
    //       const handler = setTimeout(() => {
    //         setDebouncedValue(value);
    //       }, delay);
    
    //       return () => {
    //         clearTimeout(handler);
    //       };
    //     }, [value, delay]);
    
    //     return debouncedValue;
    //   };

    
    useEffect(() => {
        qrCode.current = new QRCodeStyling(options);
        if (ref.current) {
          qrCode.current.append(ref.current);
        }
        return () => {
          if (ref.current) {
            ref.current.innerHTML = '';
          }
        };
      }, [options]);

    // useEffect(() => {
    //     if (qrCode.current) {
    //       setIsLoading(true);
    //       qrCode.current.update(debouncedOptions);
    //       setIsLoading(false);
    //     }
    //   }, [debouncedOptions]);            

      useEffect(() => {
        const updateOptions = async () => {
          if (businessGroupState.currentBusinessGroup?.subdomain) {
            setIsLoading(true);
      
            const timeoutId = setTimeout(() => {
              setOptions(prevOptions => ({
                ...prevOptions,
                data: "https://ar.skidattl.com/?id=" + businessGroupState.currentBusinessGroup?.subdomain
              }));
              setIsLoading(false);
            }, 1000);
            
            return () => clearTimeout(timeoutId);
          } else {
            setIsLoading(false);
          }
        };
      
        updateOptions();
      }, [businessGroupState.currentBusinessGroup]);
      
      
    
      const onExtensionChange = (event: SelectChangeEvent) => {
        setFileExt(event.target.value as 'png');
      };

      const onDownloadClick = () => {
        if (qrCode.current) {
            // Temporarily update options for download
            const originalOptions = { ...options };
            setOptions(prevOptions => ({
                ...prevOptions,
                width: downloadWidth,
                height: downloadHeight,
                type: 'canvas' as DrawType,
                
            }));
            qrCode.current.update({
                ...options,
                width: downloadWidth,
                height: downloadHeight,
                type: 'canvas' as DrawType,
            });

            // Download the QR code
            qrCode.current.download({ extension: 'png' });

            // Revert back to original size
            setOptions(originalOptions);
        }
    };

      const handleBGColorChange = (newValue: string) => {
        setIsLoading(true);
        setOptions(prevOptions => ({
            ...prevOptions,
            backgroundOptions: {
                ...prevOptions.backgroundOptions,
                color: newValue
            }
        }));
        setThemeColor(newValue);
      };

      const handleDotColorChange = (newValue: string) => {
        setOptions(prevOptions => ({
            ...prevOptions,
            dotsOptions: {
                ...prevOptions.dotsOptions,
                color: newValue
            },
            cornersSquareOptions: {
                ...prevOptions.cornersSquareOptions,
                color: newValue
            },
            cornersDotOptions: {
                ...prevOptions.cornersDotOptions,
                color: newValue
            }
        }));
        setDotColor(newValue);
      };

      const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = event.target.files![0];
        
        setCurrentIconFile(inputFile);
        setOptions(prevOptions => ({
          ...prevOptions,
          image: URL.createObjectURL(inputFile)
        }));
        setIconError("");
      };

    // const handleIconChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //     const inputFile = event.target.files![0];
    //     setCurrentIconFile(inputFile);
        
    //     const reader = new FileReader();
    //     reader.onloadend = () => {
    //       const blobUrl = URL.createObjectURL(new Blob([reader.result as ArrayBuffer], { type: inputFile.type }));
      
    //       setOptions(prevOptions => ({
    //         ...prevOptions,
    //         image: blobUrl
    //       }));
    //       setIconError("");
    //     };
      
    //     if (inputFile) {
    //       reader.readAsArrayBuffer(inputFile); // Read file
    //     }
    //   };
      return (
      <Card
        sx={{
          padding: 3,
          backgroundColor: "white",
        }}
      >
      <Typography sx={{marginX: 2}} variant="h6">QR Code</Typography>
      <Box sx={{ display: "flex", gap: 6 }}>
          <Box sx={{ display: "flex", flexDirection: 'column', alignItems: 'center'}}>
              {isLoading ? (
                <>
                  <Box sx={{
                    border: "1px solid #cbcbcb",
                    height: "250px",
                    width: "250px",
                    display: "flex",
                    marginBottom: 2,
                    justifyContent: "center",
                    alignItems: "center"
                  }}>
                  <CircularProgress />
                  </Box>
                  
                </>
              ) : (
                  <Box ref={ref} sx={{
                      border: "1px solid #cbcbcb",
                      borderRadius: "4px",
                      width: "250px",
                      height: "250px",
                      display: "flex",
                      marginBottom: 2
                  }} 
                  />
              )}
              <Button variant="contained" sx={{ paddingX: 4, width: "250px", marginBottom: 4 }} onClick={onDownloadClick}>
                  <DownloadIcon/>Download
              </Button>
              {/* <InputLabel id="select-file">File Type</InputLabel>
              <Select
                  labelId="select-file"
                  id="file-type-input"
                  label="File Type"
                  size="small"
                  defaultValue={"svg"}
                  onChange={onExtensionChange}
              >
                  <MenuItem value={"svg"}>.svg</MenuItem>
                  <MenuItem value={"png"}>.png</MenuItem>
                  <MenuItem value={"jpeg"}>.jpeg</MenuItem>
                  <MenuItem value={"webp"}>.webp</MenuItem>
              </Select> */}
          </Box>
        <Box sx={{
          marginBottom: 0,
          display: "flex",
          flexDirection: "column",
          rowGap: 3,
          width: "100%",
          maxWidth: "310px",
          }}
        >
          <Box mt={0} display="flex" alignItems="center" gap={2}>
              <TextField
                  id="upload-icon-qr"
                  label="Icon (256x256).png"
                  value={currentIconFile ? currentIconFile.name : '/skidattl-icon.png'}
                  size="small"
                  variant="outlined"
                  inputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
              />
              <label htmlFor="file-input-icon-qr" style={{  }}>
                  <Button variant="outlined" component="span" sx={{ width: "100px" }}>
                      Browse
                  </Button>
              </label>
              <input
                  id="file-input-icon-qr"
                  type="file"
                  accept=".png,.jpg,jpeg"
                  style={{ display: 'none' }}
                  onChange={handleIconChange}
              />
              </Box>
              <Box sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  justifyContent: "space-between",
                  width: "100%",
                  maxWidth: "202px",
                  }}
              >
              <MuiColorInput
                  value={themeColor}
                  label="Background Color"
                  size="small"
                  format="hex8"
                  onChange={handleBGColorChange}
              />
              <MuiColorInput
                  value={dotColor}
                  label="QR Color"
                  size="small"
                  format="hex8"
                  onChange={handleDotColorChange}
              />
            {/* <Box sx={{ display: "flex", flexDirection: "row", width: "200px", gap: 3}}>
              <Button variant="outlined" sx={{ paddingX: 4, width: "100px" }} >
                  Cancel
              </Button>
              <Button variant="contained" sx={{ paddingX: 4, width: "100px" }}>
                  Save
              </Button>
            </Box> */}
          </Box>
        </Box>
      </Box>
      </Card>
    );
};

export default QRCodeGen;
